/**
 * Fullstory Privacy Utilities
 *
 * Core constants and utilities for Fullstory privacy integration.
 * For most use cases, prefer the wrapper components from `components.js`
 */

import { PRIVACY_LEVELS } from './constants'

/**
 * Detects if a string contains an email pattern
 * Useful for automatically applying masks to displayed content
 *
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains an email pattern
 *
 * @example
 * containsEmail('Contact: john@example.com')
 * // Returns: true
 */
const containsEmail = (text) => {
  if (!text || typeof text !== 'string') return false

  // Simple email pattern detection
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/
  return emailPattern.test(text)
}

/**
 * Detects if a string contains a phone number pattern
 *
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains a phone number pattern
 *
 * @example
 * containsPhoneNumber('+1 (555) 123-4567')
 * // Returns: true
 */
const containsPhoneNumber = (text) => {
  if (!text || typeof text !== 'string') return false

  // Simple phone pattern detection (various formats)
  const phonePattern = /(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/
  return phonePattern.test(text)
}

/**
 * Determines appropriate privacy level for displayed text
 * Used by FullStoryAutoMask component for automatic PII detection
 *
 * @param {string} text - Text to analyze
 * @returns {string} Recommended privacy level ('mask' if PII detected, null otherwise)
 *
 * @example
 * getRecommendedPrivacyLevel('john@example.com')
 * // Returns: 'mask'
 */
export const getRecommendedPrivacyLevel = (text) => {
  if (containsEmail(text) || containsPhoneNumber(text)) {
    return PRIVACY_LEVELS.MASK
  }
  return null
}
